import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClock, CircleHelp, Clock3, MessageCircle, MoreHorizontal, Phone, X } from 'lucide-react';
import { quickTimes, telHref, toLocalInput, whatsappHref } from './crmWork';
import './stage-kit.css';

// AUTO-CRM-32: small shared pieces for the CRM's working screens.

// Plain-language answers for the words a first-time CRM user meets.
export const GLOSSARY = {
  lead: { title: 'Lead', body: 'A person or business who might buy from you: an enquiry, a walk-in, a referral. Each lead has an owner and sits in one stage.' },
  pipeline: { title: 'Pipeline', body: 'Your sales process from first enquiry to won or lost. Most businesses need one; add more for very different kinds of sales.' },
  stage: { title: 'Stage', body: 'A step in the pipeline, such as New enquiry or Proposal sent. Move a lead to the next stage as it progresses.' },
  step: { title: 'Steps', body: 'The checklist inside a stage: “Call within 1 hour”, “Share price list”. Steps marked Automation are done by an agent.' },
  followup: { title: 'Follow-up', body: 'A call, meeting or message you promise to do at a set time. Every open lead should have one, so nobody is forgotten.' },
  owner: { title: 'Owner', body: 'The teammate responsible for the lead. Leads without an owner are shared work that anyone can pick up.' },
  winrate: { title: 'Win rate', body: 'Of the leads that finished (won or lost), how many you won.' },
  leadtable: { title: 'Lead table', body: 'The information you collect for each lead (phone, budget, city…). It decides the form fields and the columns in the lead list.' },
};

export function HelpTip({ term, children }) {
  const entry = GLOSSARY[term];
  const [open, setOpen] = useState(false);
  const id = useId();
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  if (!entry) return children || null;
  return (
    <span className="crm-help" ref={rootRef}>
      {children}
      <button type="button" className="crm-help-btn" aria-label={`What is ${entry.title.toLowerCase()}?`} aria-expanded={open} aria-controls={id} onClick={() => setOpen((value) => !value)}>
        <CircleHelp size={13} aria-hidden="true" />
      </button>
      {open && (
        <span className="crm-help-pop" role="note" id={id}>
          <strong>{entry.title}</strong>
          {entry.body}
        </span>
      )}
    </span>
  );
}

export function StageDialog({ title, subtitle, icon: Icon, onClose, children, footer, width = 520, busy = false, labelledBy }) {
  const panelRef = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement;
    const first = panelRef.current?.querySelector('input, textarea, select, button:not([data-close])');
    first?.focus();
    return () => previous?.focus?.();
  }, []);
  return createPortal(
    <div className="ws-dialog-backdrop ws-overlay crm-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <div ref={panelRef} className="ws-dialog crm-kit-dialog" role="dialog" aria-modal="true" aria-labelledby={labelledBy || titleId} style={{ width: `min(${width}px, 100%)` }}
        onKeyDown={(event) => { if (event.key === 'Escape' && !busy) { event.stopPropagation(); onClose(); } }}>
        <header className="crm-kit-dialog-head">
          {Icon && <span className="crm-kit-dialog-icon" aria-hidden="true"><Icon size={18} /></span>}
          <div className="ws-grow">
            <h2 id={titleId} className="ws-h2">{title}</h2>
            {subtitle && <p className="ws-muted">{subtitle}</p>}
          </div>
          <button type="button" data-close className="crm-icon-btn crm-icon-btn--sm" onClick={onClose} disabled={busy} aria-label="Close"><X size={15} /></button>
        </header>
        <div className="crm-kit-dialog-body">{children}</div>
        {footer && <footer className="crm-kit-dialog-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

// Chips for common times plus an exact picker. Value is a Date or null.
export function WhenPicker({ value, onChange, label = 'When', allowNone = false }) {
  const options = quickTimes();
  const matched = options.find((option) => value && Math.abs(option.at.getTime() - value.getTime()) < 60000);
  return (
    <fieldset className="crm-when">
      <legend className="crm-kit-label">{label}</legend>
      <div className="crm-chip-row">
        {allowNone && <button type="button" className={`crm-choice${!value ? ' is-on' : ''}`} aria-pressed={!value} onClick={() => onChange(null)}>No follow-up</button>}
        {options.map((option) => (
          <button key={option.id} type="button" className={`crm-choice${matched?.id === option.id ? ' is-on' : ''}`} aria-pressed={matched?.id === option.id} onClick={() => onChange(option.at)}>
            {option.label}
          </button>
        ))}
      </div>
      <label className="crm-when-exact">
        <CalendarClock size={15} aria-hidden="true" />
        <span className="ws-sr-only">Exact date and time</span>
        <input type="datetime-local" value={value ? toLocalInput(value) : ''} onChange={(event) => onChange(event.target.value ? new Date(event.target.value) : null)} />
      </label>
    </fieldset>
  );
}

export function SnoozeMenu({ onPick, disabled = false, label = 'Snooze', compact = false, buttonClass = 'ws-btn ws-btn--quiet ws-btn--sm' }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const pick = (date) => { setOpen(false); setCustom(''); onPick(date); };
  return (
    <span className="ws-dd-root" ref={rootRef}>
      <button type="button" className={compact ? 'crm-icon-btn crm-icon-btn--sm' : buttonClass} disabled={disabled} aria-haspopup="menu" aria-expanded={open} aria-label={compact ? label : undefined} title={compact ? label : undefined} onClick={() => setOpen((value) => !value)}>
        <Clock3 size={compact ? 14 : 13} />{!compact && label}
      </button>
      {open && (
        <div className="ws-row-menu crm-snooze" role="menu" aria-label="Move to">
          <div className="ws-menu-label">Move to</div>
          {quickTimes().map((option) => <button key={option.id} type="button" role="menuitem" className="ws-menu-item" onClick={() => pick(option.at)}>{option.label}</button>)}
          <div className="ws-menu-sep" />
          <label className="crm-snooze-custom">
            <span className="ws-sr-only">Pick a date and time</span>
            <input type="datetime-local" value={custom} onChange={(event) => setCustom(event.target.value)} />
            <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" disabled={!custom} onClick={() => pick(new Date(custom))}>Set</button>
          </label>
        </div>
      )}
    </span>
  );
}

export function ContactButtons({ phone, name, size = 'sm' }) {
  const tel = telHref(phone);
  const wa = whatsappHref(phone);
  const who = name ? ` ${name}` : '';
  if (!tel) return <span className="crm-contact-missing" title="Add a phone number to call or message">No phone</span>;
  return (
    <span className="crm-contact">
      <a className={`crm-icon-btn ${size === 'sm' ? 'crm-icon-btn--sm' : ''}`} href={tel} aria-label={`Call${who}`} title={`Call ${phone}`}><Phone size={14} /></a>
      <a className={`crm-icon-btn crm-icon-btn--wa ${size === 'sm' ? 'crm-icon-btn--sm' : ''}`} href={wa} target="_blank" rel="noreferrer" aria-label={`WhatsApp${who}`} title="Open WhatsApp"><MessageCircle size={14} /></a>
    </span>
  );
}

// A "⋯" menu for rows and cards. It renders in a portal so cards with hidden
// overflow never clip it. items: [{ label, icon, onClick, danger, disabled, title }]
// (falsy entries are skipped; 'separator' draws a line).
export function ActionMenu({ label, items, className = 'crm-icon-btn crm-icon-btn--sm', icon: Icon = MoreHorizontal, children }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: -9999, left: -9999 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const visible = (items || []).filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onPointer = (event) => { if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) close(); };
    const onKey = (event) => { if (event.key === 'Escape') { close(); triggerRef.current?.focus(); } };
    const onScroll = (event) => { if (!menuRef.current?.contains(event.target)) close(); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', onScroll, true);
    menuRef.current?.querySelector('button:not(:disabled)')?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const left = Math.min(Math.max(12, trigger.right - menu.width), window.innerWidth - menu.width - 12);
    const top = window.innerHeight - trigger.bottom >= menu.height + 12 ? trigger.bottom + 6 : Math.max(12, trigger.top - menu.height - 6);
    setPosition({ top, left });
  }, [open]);

  const onMenuKey = (event) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const buttons = [...menuRef.current.querySelectorAll('button:not(:disabled)')];
    const index = buttons.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? (index + 1) % buttons.length : (index - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  if (!visible.length) return null;
  return (
    <>
      <button ref={triggerRef} type="button" className={className} aria-label={label} title={children ? undefined : label} aria-haspopup="menu" aria-expanded={open} onClick={(event) => { event.stopPropagation(); setOpen((value) => !value); }}>
        {children || <Icon size={15} />}
      </button>
      {open && createPortal(
        <div ref={menuRef} className="ws-popover ws-overlay crm-action-menu" role="menu" aria-label={label} style={{ top: position.top, left: position.left }} onKeyDown={onMenuKey}>
          {visible.map((item, index) => (item === 'separator'
            ? <div key={`sep-${index}`} className="ws-menu-sep" role="separator" />
            : (
              <button key={item.label} type="button" role="menuitem" className={`ws-menu-item${item.danger ? ' is-danger' : ''}`} disabled={item.disabled} title={item.title}
                onClick={() => { setOpen(false); item.onClick?.(); }}>
                {item.icon && <item.icon size={15} />}{item.label}
              </button>
            )))}
        </div>,
        document.body,
      )}
    </>
  );
}
